import { Suspense } from 'react';

import { Navigate, Outlet, Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { FeatureFlagGuard } from '@/components/routes/FeatureFlagGuard';
import { PersonalIndexRedirect } from '@/components/routes/PersonalRedirect';
import { SessionExtendRedirect } from '@/components/routes/SessionExtendRedirect';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';

// ============================================================================
// User-scoped routes: /personal/* (sessions, queries, RFIs, notifications,
// the missions/runs tree), /rfis/create, /extend-session, and the
// /personal/:tab workspace shell. Mounted inside ProtectedRoute → TOSGuard →
// AppLayout in App.tsx; the guards live on that shared scaffold, not here.
// (/settings moved to settings.routes.tsx — it must not require org
// selection, per GW-452.)
//
// GW-10596: the standalone /notifications route (GW-8889) was removed —
// notifications is now the "notifications" tab inside the /personal
// TabbedDetailPage shell (see PersonalWorkspacePage.tsx), so a bare
// /notifications visit now redirects to /personal/notifications below
// (existing bookmarks/links from HeaderHost/SettingsPage still work).
// ============================================================================

// GW-1728: DatasetQueryPage removed - query forms are now inline on DatasetDetailPage
// GW-10598: MySessionsPage is no longer imported here — it is rendered as the
// 'sessions' tab content by PersonalWorkspacePage (GW-8139) directly, the
// same pattern already used for Queries/Files (see the GW-8139 comment
// below). The standalone "/personal/sessions" route that used to render it
// here shadowed the shell route "/personal/:tab" (React Router v6 ranks
// static path segments above dynamic ones regardless of declaration order),
// so /personal/sessions never rendered the "My Work" TabbedDetailPage shell.
const ContainerTerminalPage = lazyWithRetry(() =>
  import('@/pages/protected/sessions/ContainerTerminalPage').then((m) => ({
    default: m.ContainerTerminalPage,
  }))
);
const ContainerSessionPage = lazyWithRetry(() =>
  import('@/pages/protected/sessions/ContainerSessionPage').then((m) => ({
    default: m.ContainerSessionPage,
  }))
);
// GW-1807: Personal query pages (cross-dataset view)
// GW-8139: PersonalQueriesPage/MyFilesPage/PersonalRFIListPage are now
// imported directly by PersonalWorkspacePage (the shared tab shell) rather
// than lazily here — only PersonalQueryDetailPage (a detail route) remains.
const PersonalQueryDetailPage = lazyWithRetry(() =>
  import('@/pages/personal/PersonalQueryDetailPage').then((m) => ({
    default: m.PersonalQueryDetailPage,
  }))
);
// GW-3059: RFI buyer pages
// GW-8139: Shared TabbedDetailPage shell for /personal (target IA §4).
const PersonalWorkspacePage = lazyWithRetry(() =>
  import('@/pages/personal/PersonalWorkspacePage').then((m) => ({
    default: m.PersonalWorkspacePage,
  }))
);
const RFIDetailPage = lazyWithRetry(() =>
  import('@/pages/buyer/RFIDetailPage').then((m) => ({
    default: m.RFIDetailPage,
  }))
);
const RFICreatePage = lazyWithRetry(() =>
  import('@/pages/buyer/RFICreatePage').then((m) => ({
    default: m.RFICreatePage,
  }))
);
// GW-5445 (P1-3): Personal mission pages, gated by missions_marketplace
// feature flag. MissionListPage itself is now imported directly by
// PersonalWorkspacePage (GW-8139) rather than lazily here — only the detail
// pages below remain independent top-level routes. GW-8293 (GW-8255 Task 36):
// MissionNewPage/the legacy /personal/missions/new route were deleted —
// NewMissionModal (GW-8288) on /personal/missions is the sole "start a
// mission" entry point now.
// GW-8054: Per-mission runs table (D8) mounts at
// /personal/missions/:missionId per docs/target-ia-outline.md §4. GW-8027
// (Phase 3, run-detail-routes)/GW-10160: the run-detail routes below
// (.../runs/:runId) are the per-run mount point.
const MissionRunsPage = lazyWithRetry(() =>
  import('@/pages/missions/MissionRunsPage').then((m) => ({
    default: m.MissionRunsPage,
  }))
);
// GW-8067: PROD-CORE-004 cost ledger, at /runs/:runId/cost-ledger.
const CostLedgerPage = lazyWithRetry(() =>
  import('@/pages/missions/CostLedgerPage').then((m) => ({
    default: m.CostLedgerPage,
  }))
);
// GW-8291 (GW-8255 Task 34): run detail — status-driven swap between
// MissionRunPage (in-progress) and RunResultsPage (completed), at
// /runs/:runId. Replaces the GW-8027 Phase 3 wiring that always rendered
// RunResultsPage regardless of status.
const RunDetailPage = lazyWithRetry(() =>
  import('@/pages/missions/RunDetailPage').then((m) => ({
    default: m.RunDetailPage,
  }))
);
// GW-8027 (Phase 3, run-detail-routes): PROD-CORE-001 entity graph, at
// /runs/:runId/entity-graph.
const EntityGraphPage = lazyWithRetry(() =>
  import('@/pages/missions/EntityGraphPage').then((m) => ({
    default: m.EntityGraphPage,
  }))
);
// GW-10160 (GW-8027 Phase 3, run-detail-routes): PROD-CORE-002 evidence
// register, at /runs/:runId/evidence-register.
const EvidenceRegisterPage = lazyWithRetry(() =>
  import('@/pages/missions/EvidenceRegisterPage').then((m) => ({
    default: m.EvidenceRegisterPage,
  }))
);
// GW-8027 (Phase 3, run-detail-routes): PROD-CORE-003 audit trail, at
// /runs/:runId/audit-trail.
const AuditTrailPage = lazyWithRetry(() =>
  import('@/pages/missions/AuditTrailPage').then((m) => ({
    default: m.AuditTrailPage,
  }))
);
// GW-10160 (GW-8027 Phase 3, run-detail-routes): PROD-CORE-005 event
// timeline, at /runs/:runId/event-timeline.
const EventTimelinePage = lazyWithRetry(() =>
  import('@/pages/missions/EventTimelinePage').then((m) => ({
    default: m.EventTimelinePage,
  }))
);
// GW-10160: single canonical dispatch point for every run-scoped product, at
// /runs/:runId/products/:productId. Dispatches per productId internally
// (PROD-027 Subject Profile, PROD-006 due-diligence report, or any other
// known product) rather than each product having its own bespoke route —
// this replaces the former dedicated /runs/:runId/subject-profile route.
const RunProductReportPage = lazyWithRetry(() =>
  import('@/pages/missions/RunProductReportPage').then((m) => ({
    default: m.RunProductReportPage,
  }))
);

// GW-13897: stub run page at /personal/inquiries/:inquiryId.
// Full run page is GW-13913.
const InquiryRunPage = lazyWithRetry(() =>
  import('@/pages/personal/InquiryRunPage').then((m) => ({
    default: m.InquiryRunPage,
  }))
);

function NoPaddingOutlet() {
  return (
    <div className="-m-6">
      <Outlet />
    </div>
  );
}

export const personalRoutes = (
  <>
    {/* GW-10596: bare /notifications (GW-8889's former standalone route)
        now redirects to the notifications tab inside the /personal shell —
        keeps existing bookmarks/links (HeaderHost, SettingsPage) working. */}
    <Route path="/notifications" element={<Navigate to="/personal/notifications" replace />} />

    {/* Personal routes (GW-422: Sessions and Settings under /personal).
        GW-10598: the standalone "/personal/sessions" route that used to
        render MySessionsPage directly here was removed — it shadowed the
        "/personal/:tab" shell route below (static path segments always rank
        above dynamic ones in React Router v6), so direct navigation to
        /personal/sessions bypassed the "My Work" TabbedDetailPage chrome.
        This now falls through to "/personal/:tab" exactly like
        /personal/queries and /personal/files already did (GW-8139). */}

    {/* GW-9839: Redirect from SDK fallback URL to correct session extend route */}
    <Route path="/extend-session" element={<SessionExtendRedirect />} />
    {/* GW-1807: Personal query detail page (gated by feature flag) */}
    <Route
      path="/personal/queries/:queryId"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/personal/sessions">
          <Suspense fallback={<PageSkeleton />}>
            <PersonalQueryDetailPage />
          </Suspense>
        </FeatureFlagGuard>
      }
    />
    {/* GW-3059: RFI buyer pages. GW-8665: gated behind services_marketplace. */}
    <Route
      path="/personal/rfis/:rfiId"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <Suspense fallback={<PageSkeleton />}>
            <RFIDetailPage />
          </Suspense>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/rfis/create/:serviceSlug"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <Suspense fallback={<PageSkeleton />}>
            <RFICreatePage />
          </Suspense>
        </FeatureFlagGuard>
      }
    />
    {/* GW-6049: Container session routes - terminal and web access modes.
        GW-8283/D27, GW-8286: mounted only at
        /personal/sessions/:sessionId/* (the canonical path
        under the /personal/sessions tab) — the legacy bare
        /sessions/:sessionId/* paths were retired per
        target-ia-outline.md §12; SessionActionsMenu/
        MySessionsPage/ApplicationDetailPage now navigate here
        directly. */}
    <Route
      path="/personal/sessions/:sessionId/terminal"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <ContainerTerminalPage />
        </Suspense>
      }
    />
    <Route
      path="/personal/sessions/:sessionId/web"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <ContainerSessionPage />
        </Suspense>
      }
    />

    {/* GW-5445 (P1-3): Personal mission detail routes behind
        missions_marketplace flag. Replaces legacy
        /secret/missions/* tree. GW-8139: the bare
        "personal/missions" list-tab route moved into the
        shared PersonalWorkspacePage shell (below, GW-8027
        Phase 3: now nested INSIDE this same AppLayout block
        rather than self-composing its own) — only the detail
        routes below stay here. GW-8293 (GW-8255 Task 36): the
        legacy "personal/missions/new" single-page form route
        was deleted outright (no redirect, per PRP-GW8255's
        standing no-back-compat decision) — NewMissionModal
        (GW-8288) on /personal/missions is the only "start a
        mission" entry point now.
        new/products use NoPaddingOutlet for full-bleed layout. */}
    <Route element={<NoPaddingOutlet />}>
      <Route
        path="personal/missions/:missionId"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <MissionRunsPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-8292: /runs list — the PRP/wireframe spell this D8
            runs table route with an explicit "/runs" segment
            (FR-6, Task 35); mount the same MissionRunsPage here
            alongside the bare :missionId route above so both
            spellings resolve to the identical table. */}
      <Route
        path="personal/missions/:missionId/runs"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <MissionRunsPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-8291 (GW-8255 Task 34): run detail — the mount
            point MissionRunsPage's per-run rows navigate into.
            Status-driven swap between MissionRunPage
            (in-progress) and RunResultsPage (completed) lives
            in RunDetailPage. */}
      <Route
        path="personal/missions/:missionId/runs/:runId"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <RunDetailPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-8027 (Phase 3, run-detail-routes): PROD-CORE-001
            entity graph for a mission run. */}
      <Route
        path="personal/missions/:missionId/runs/:runId/entity-graph"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <EntityGraphPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-10160 (GW-8027 Phase 3, run-detail-routes): PROD-CORE-002
            evidence register for a mission run. */}
      <Route
        path="personal/missions/:missionId/runs/:runId/evidence-register"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <EvidenceRegisterPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-8027 (Phase 3, run-detail-routes): PROD-CORE-003
            audit trail for a mission run. */}
      <Route
        path="personal/missions/:missionId/runs/:runId/audit-trail"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <AuditTrailPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-10160: single canonical route for every run-scoped product
            (PROD-027 Subject Profile, PROD-006 due-diligence report, or any
            other known product) — RunProductReportPage dispatches on
            productId internally. Replaces the former separate
            /runs/:runId/subject-profile route. */}
      <Route
        path="personal/missions/:missionId/runs/:runId/products/:productId"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <RunProductReportPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-8067: PROD-CORE-004 cost ledger for a mission run. */}
      <Route
        path="personal/missions/:missionId/runs/:runId/cost-ledger"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <CostLedgerPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-10160 (GW-8027 Phase 3, run-detail-routes): PROD-CORE-005
            event timeline for a mission run. */}
      <Route
        path="personal/missions/:missionId/runs/:runId/event-timeline"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <EventTimelinePage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-13897: stub Inquiry run page. Full run page is GW-13913. */}
      <Route
        path="personal/inquiries/:inquiryId"
        element={
          <FeatureFlagGuard flagName="inquiry" fallbackRoute="/personal/missions">
            <Suspense fallback={<PageSkeleton />}>
              <InquiryRunPage />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
    </Route>

    {/* GW-8027 Phase 3: Shared TabbedDetailPage shells for
        /personal, /organization, /publisher, /settings
        (target IA §4-6). These used to self-compose their own
        AppLayout via useAppChromeSlots (GW-8139/GW-8051) because
        they weren't nested under this shared AppLayout route
        wrapper — that duplicated the sidebar/header chrome from
        Sidebar.tsx's independent implementation. Now nested here
        like every other protected route, they render bare
        content and share this single AppLayout/Sidebar.tsx
        instance. Path-segment tabs render the previously-
        independent tab pages as tab content; each bare parent
        path redirects to its documented default tab. */}
    {/* GW-8665: falls back to /personal/sessions when
        missions_marketplace is disabled — see
        PersonalIndexRedirect. */}
    <Route path="/personal" element={<PersonalIndexRedirect />} />
    <Route
      path="/personal/:tab"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <PersonalWorkspacePage />
        </Suspense>
      }
    />
  </>
);
